import React, { useContext } from 'react';
import { Chart } from "react-google-charts";
import { Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart, LineChart, LineSeriesType } from '@mui/x-charts';
import { MakeOptional } from '@mui/x-charts/models/helpers';
import { groupBy, successRunFilter, calculateSuccessRate, stringDateToDate } from '../../utils/utils';

import Run from '../../types/run';
import Game from '../../types/game';
import { formatDate, groupByDateAcc, sortByDate } from '../../utils/utils';

import RunsContext from "../../store/runs-context";
import GamesContext from "../../store/games-context";

import styles from './visual.module.scss';

const createDateGroups = (startDate: string, endDate: string, days = 7): DateObject[] => {
    let currentDate = stringDateToDate(endDate);
    let startDate2 = stringDateToDate(startDate);
    const dateGroups = [] as DateObject[];
    let initial = true;
    while (currentDate > startDate2) {
        if (!initial) {
            currentDate.setDate(currentDate.getDate() - 1);
        } else {
            initial = false;
        }
        const currentDateJS = new Date(currentDate);
        currentDate.setDate(currentDateJS.getDate() - (days - 1));
        dateGroups.push({
            startDate: formatDate(currentDate),
            endDate: formatDate(currentDateJS)
        });
    }
    return dateGroups;
};

interface DateObject {
    startDate: string | Date,
    endDate: string | Date,
};

interface Variant {
    [key: string]: [];
}

const createDataForGroupsPerGame = (data: Run[], groups: DateObject[], games: Game[]) => {
    const dataGrouped: Run[][][] = [];
    groups.forEach(() => {
        const groupData: Run[][] = [];
        games.forEach(() => {
            groupData.push([] as Run[]);
        });
        dataGrouped.push(groupData); // Adding all the games
    });
    data.forEach(dateItem => {
        groups.forEach((group, groupIndex) => {
            if (dateItem.date && (dateItem.date >= group.startDate && dateItem.date <= group.endDate)) {
                dataGrouped[groupIndex][dateItem.selectedGame]?.push(dateItem);
            }
        });
    });
    
    return dataGrouped;
}

const getLastWeekData = (runs: Run[]) => {
    const weekEndDateNoTyped = runs[runs.length - 1].date;
    let weekEndDate;
    if(typeof weekEndDateNoTyped === 'string') {
        weekEndDate = stringDateToDate(weekEndDateNoTyped);
    } else {
        weekEndDate = weekEndDateNoTyped;
    }
    let weekStartDate = weekEndDate;
    if (typeof weekStartDate === 'undefined') {
        return;
    }
    weekStartDate.setDate(weekStartDate.getDate() - 7);
    const lastWeekRuns = runs.filter(run => {
        let runDateTimestamp : number = 0;
        if (!run.date || typeof weekStartDate === 'undefined') {
            return false;
        }
        runDateTimestamp = stringDateToDate(run.date).getTime();
        return runDateTimestamp > weekStartDate.getTime();
    });
    const splicedSuccesfulRuns = lastWeekRuns.filter(successRunFilter);
    const runsGroupedByDate = groupBy(splicedSuccesfulRuns as [], 'date');
    const runsTotalGroupedByDate = groupBy(lastWeekRuns as [], 'date');
    const lastWeekRunsGroupedByDate = {};
    const lastWeekTotalRunsGroupedByDate = {};
    for (let i = 0; i < 7; i++) {
        weekStartDate.setDate(weekStartDate.getDate() + 1);
        const weekStartDateYYYYMMDD: string = formatDate(weekStartDate);
        const value = (runsGroupedByDate as Variant)[weekStartDateYYYYMMDD] || [];
        const valueTotal = (runsTotalGroupedByDate as Variant)[weekStartDateYYYYMMDD] || [];
        (lastWeekRunsGroupedByDate as Variant)[weekStartDateYYYYMMDD] = value;
        (lastWeekTotalRunsGroupedByDate as Variant)[weekStartDateYYYYMMDD] = valueTotal;
    }
    let labels = Object.keys(lastWeekRunsGroupedByDate).sort((a: string, b: string) => parseInt(a, 10) - parseInt(b, 10));
    labels = labels.map(label => stringDateToDate(label).toLocaleDateString())
    const runsGroupedByDateValues: Run[][] = Object.values(lastWeekRunsGroupedByDate);
    const totalRunsGroupedByDateValues: Run[][] = Object.values(lastWeekTotalRunsGroupedByDate);
    let data = runsGroupedByDateValues.map(run => run.length);
    let dataRatio = runsGroupedByDateValues.map((run, index) => {
        return totalRunsGroupedByDateValues[index]?.length ? (run.length / totalRunsGroupedByDateValues[index].length) * 100 : 0
    });

    let lastWeekRunsGrouped = data.map((dataItem, index) => [labels[index], dataItem]);

    let lastWeekRunsGroupedRatio = dataRatio.map((dataItem, index) => [labels[index], dataItem]);
    lastWeekRunsGroupedRatio = [['Day', 'Succesful Runs %'], ...lastWeekRunsGroupedRatio];

    return { lastWeekRuns: lastWeekRunsGrouped, lastWeekRunsRatio: lastWeekRunsGroupedRatio, weekLabels: labels };

};

const getPerWeekRuns = ({ succesfulRunsWeekly, dateGroups, games }: { succesfulRunsWeekly: Run[][][], dateGroups: DateObject[], games: Game[] }) => {
    const outputData: { stack: string; label: string; data: number[]; }[] = [];
    const weeklyLabels = [] as unknown as [string];
    succesfulRunsWeekly.forEach((weeklyRuns, weekIndex) => {
        // const totalAcc = weeklyRuns.reduce((acc, item) => acc += item.length, 0);
        weeklyLabels.push(`${stringDateToDate(dateGroups[weekIndex].startDate.toString()).toLocaleDateString()} to ${stringDateToDate(dateGroups[weekIndex].endDate.toString()).toLocaleDateString()}`)
        weeklyRuns.forEach((gameRuns, gameIndex) => {
            if(!(outputData)[gameIndex]) {
                outputData[gameIndex] = { 'stack': 'Games', 'label': games[gameIndex].acronym, data: [] };
            }
            outputData[gameIndex].data.push(gameRuns.length);
        })
    });
    return { data: outputData, weeklyLabels: weeklyLabels };
};
const getPerWeekRunsRatio = ({ runsWeekly, succesfulRunsWeekly, dateGroups, games }: { runsWeekly: Run[][][], succesfulRunsWeekly: Run[][][], dateGroups: DateObject[], games: Game[] }) => {
    const outputRatioData: { stack: string; label: string; data: number[]; }[] = [];
    const weeklyLabels = [] as unknown as [string];
    runsWeekly.forEach((weeklyRuns, weekIndex) => {
        const succesfulweeklyRuns = succesfulRunsWeekly[weekIndex];
        weeklyLabels.push(`${stringDateToDate(dateGroups[weekIndex].startDate.toString()).toLocaleDateString()} to ${stringDateToDate(dateGroups[weekIndex].endDate.toString()).toLocaleDateString()}`)
        weeklyRuns.forEach((gameRuns, gameIndex) => {
            if(!(outputRatioData)[gameIndex]) {
                outputRatioData[gameIndex] = { 'stack': 'Games', 'label': games[gameIndex].acronym, data: [] };
            }
            outputRatioData[gameIndex].data.push(((succesfulweeklyRuns[gameIndex].length / games.length) * 100));
        });

    });
    return { data: outputRatioData, weeklyLabels: weeklyLabels };
};

const getPerWeekData = (runs: Run[], initialDate: string, games: Game[]) => {
    const weekEndDate = runs[runs.length - 1].date;
    const succesfulRuns = runs.filter(successRunFilter);

    const dateGroups: DateObject[] = createDateGroups(initialDate, formatDate(weekEndDate));
    dateGroups.reverse();
    const succesfulRunsWeekly = createDataForGroupsPerGame(succesfulRuns, dateGroups, games);
    const runsWeekly = createDataForGroupsPerGame(runs, dateGroups, games);


    const perWeekRuns = getPerWeekRuns({ succesfulRunsWeekly, dateGroups, games });
    const perWeekRunsRatio = getPerWeekRunsRatio({ runsWeekly, succesfulRunsWeekly, dateGroups, games });

    return { perWeekRuns, perWeekRunsRatio };
}

const getCalendarData = (runs: Run[]) => {
    const succesfulRuns = runs.filter(successRunFilter);
    const dataHeader = [{ type: 'date', id: 'Date' }, { type: 'number', id: 'Sucessful runs' }];
    const runsGroupedByDate = groupBy(runs as [], 'date');
    const succesfulRunsGroupedByDate = groupBy(succesfulRuns as [], 'date');
    const calendarData = Object.values(runsGroupedByDate).map((perDayRuns, index) => {
        const date: string = Object.keys(runsGroupedByDate)[index];
        const successFullRuns = (succesfulRunsGroupedByDate as any)[date]?.length || 0;
        return [stringDateToDate(date), successFullRuns];
    });
    return [dataHeader, ...calendarData];
};

const getCalendarDataRate = (runs: Run[]) => {
    const succesfulRuns = runs.filter(successRunFilter);
    const dataHeader = [{ type: 'date', id: 'Date' }, { type: 'number', id: 'Sucessful runs' }];
    const runsGroupedByDate = groupBy(runs as [], 'date');
    const succesfulRunsGroupedByDate = groupBy(succesfulRuns as [], 'date');
    const calendarData = Object.values(runsGroupedByDate).map((perDayRuns, index) => {
        const date = Object.keys(runsGroupedByDate)[index];
        const perDaySuccessfulRuns = (succesfulRunsGroupedByDate as Variant)[(date as string)];

        let ratio = calculateSuccessRate({ successfulRuns: (perDaySuccessfulRuns as Run[]), totalRuns: (perDayRuns as Run[]) });
        return [stringDateToDate(date), ratio];
    });
    return [dataHeader, ...calendarData];
};

function Visual() {
    const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
    const runContext = useContext(RunsContext);
    const gameContext = useContext(GamesContext);
    const runs = runContext.runs;
    const games = gameContext.games;
    const gamesColors = gameContext.gamesColors || [];
    if (runs.length <= 0) {
        return (<>Loading Data...</>);
    }
    const groupedRuns = runs.reduce(groupByDateAcc, []);
    const initialSortedRuns = groupedRuns?.sort(sortByDate);
    const initialDate = initialSortedRuns[0].date;
    const filteredRuns = runs.filter(run => run.endSplit === undefined);

    const lastWeedData = getLastWeekData(filteredRuns);
    let lastWeekRuns: MakeOptional<LineSeriesType, "type">[] = [];
    if (lastWeedData) {
        // Expected: [    { curve: "natural", data: [0, 5, 2, 6, 3, 9.3] },    { curve: "natural", data: [6, 3, 7, 9.5, 4, 2] },  ]
        lastWeekRuns = [{ curve: "natural", data: lastWeedData.lastWeekRuns.map(item => item[1] as number)}];
        // lastWeekRunsRatio = [{ curve: "natural", data: lastWeedData.lastWeekRunsRatio.map(item => item[1] as number)}];
    }

    const { perWeekRuns } = getPerWeekData(filteredRuns, initialDate, games);
    const yAxisLabels = perWeekRuns.weeklyLabels;

    const calendarData = getCalendarData(filteredRuns);

    const calendarDataRate = getCalendarDataRate(filteredRuns);

    return (
        <Stack>
            {!isMobile && 
                <div className={`${styles.statBlock}`}>
                    <h2 className={styles.header2}>Global Stats</h2>
                    <div style={{ display: 'flex', maxWidth: isMobile ? 400 : 1200, margin: 'auto', overflowX: 'initial' }}>
                        <Chart
                            width={isMobile ? 400 : 1200}
                            height={200}
                            chartType="Calendar"
                            loader={<div>Loading Chart</div>}
                            data={calendarData}
                            options={{
                                title: 'Successful run per day',
                                noDataPattern: {},
                                calendar: {
                                    cellSize: isMobile ? 12 : 20,
                                    cellColor: {
                                        strokeWidth: 1,
                                    },
                                    dayOfWeekLabel: {}
                                }
                            }}
                            rootProps={{ 'data-testid': '1' }}
                        />
                    </div>
                    <div style={{ display: 'flex', maxWidth: isMobile ? 400 : 1200, margin: 'auto', overflowX: 'initial' }}>
                        <Chart
                            width={isMobile ? 400 : 1200}
                            height={200}
                            chartType="Calendar"
                            loader={<div>Loading Chart</div>}
                            data={calendarDataRate}
                            options={{
                                title: 'Successful run % per day',
                                noDataPattern: {},
                                calendar: {
                                    cellSize: isMobile ? 12 : 20,
                                    cellColor: {
                                        strokeWidth: 1,
                                    },
                                    dayOfWeekLabel: {}
                                }
                            }}
                            rootProps={{ 'data-testid': '1' }}
                        />
                    </div>
                </div>
            }
            <div className={`${styles.statBlock}`}>
                <h2 className={styles.header2}>Weekly Stats</h2>
                <div style={{ display: 'flex', width: isMobile ? 350 : 1200, margin: 'auto' }}>
                    <BarChart
                        series={perWeekRuns.data}
                        yAxis={[{ scaleType: 'band', data: yAxisLabels }]}
                        colors={gamesColors}
                        width={isMobile ? 250 : 900}
                        height={200 + (perWeekRuns.weeklyLabels.length * 30)}
                        layout="horizontal"
                    />
                </div>
            </div>
            <div className={styles.statBlock}>
                <h2 className={styles.header2}>Last Week Stats</h2>
                <div style={{ display: 'flex', maxWidth: isMobile ? 350 : 1000, margin: 'auto' }}>
                    <LineChart
                        width={isMobile ? 350 : 1000}
                        height={300}
                        series={lastWeekRuns || []}
                        xAxis={[
                            {
                              data: lastWeedData?.weekLabels,
                              scaleType: 'point',
                            },
                        ]}
                    />
                </div>
                {/* <div style={{ display: 'flex', maxWidth: isMobile ? 350 : 1000, margin: 'auto' }}>
                    <LineChart
                        width={isMobile ? 350 : 1000}
                        height={300}
                        series={lastWeekRunsRatio}
                        // options={{
                        //     legend: 'none',
                        //     title: 'Last Week Succesful Rate per Day',
                        //     chartArea: { width: '70%' },
                        //     hAxis: {
                        //         title: 'Day',
                        //         minValue: 0,
                        //     },
                        //     vAxis: {
                        //         title: 'Succesful Runs %',
                        //         minValue: 0,
                        //     },
                        // }}
                    />
                </div> */}
            </div>
        </Stack>
    );


}

export default Visual;
